/**
 *
 * Timeline entry for a consultation, flat card showing all nested content
 * (vitals, notes, treatments) as stacked rows.
 *
 * Every treatment renders through `TreatmentRow`.
 */
import {
  Banknote,
  Building2,
  CalendarClock,
  ClipboardList,
  Clock,
  CopyPlus,
  EllipsisVertical,
  EyeOff,
  FileText,
  Globe,
  HeartPulse,
  History,
  LockOpen,
  LogIn,
  MessageCircleMore,
  NotebookPen,
  Paperclip,
  Printer,
  Stethoscope,
} from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import {
  APPOINTMENT_STATUS_TRANSITIONS,
  APPOINTMENT_TYPES_BY_ID,
  type ConsultationWithRelations,
  deriveConsultationStatus,
  formatClinicPrice,
  formatName,
  formatWeight,
  getClinic,
  isNoteEmpty,
  type NoteWithStaff,
  resolveDateFormat,
  type TreatmentPrescriptionContext,
  type TreatmentWithRelations,
} from '@snootsvet/shared'

import { ExpandBadge } from '@/frontend/components/expand-badge'
import { FileDownloadLink } from '@/frontend/components/file-download-link'
import { FormattedDateTime } from '@/frontend/components/formatted-datetime'
import { Badge } from '@/frontend/components/ui/badge'
import { Button } from '@/frontend/components/ui/button'
import { ConfirmButton } from '@/frontend/components/ui/confirm-button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/frontend/components/ui/dropdown-menu'
import { ExpandableContent } from '@/frontend/components/ui/expandable-content'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/frontend/components/ui/tooltip'
import { UpcomingBadge } from '@/frontend/components/upcoming-badge'
import { AppointmentActivityLog } from '@/frontend/features/appointments/appointment-activity-log'
import { AppointmentStatusBadge } from '@/frontend/features/appointments/appointment-status-badge'
import { getCategoryStyle } from '@/frontend/features/appointments/appointments.utils'
import { useActiveConsultation } from '@/frontend/features/consultations/active-consultation-context'
import { getPricedOrder } from '@/frontend/features/consultations/treatments.utils'
import {
  useGeneratePrescriptionScript,
  useRefillPrescription,
  useReprescribePrescription,
} from '@/frontend/features/patient/prescription-actions'
import { NoteContent } from '@/frontend/features/patient/timeline/note-entry'
import { TreatmentRow } from '@/frontend/features/patient/timeline/treatment-row'
import { VitalsChips } from '@/frontend/features/patient/vitals-chip'
import { committedTreatmentToLabelData } from '@/frontend/features/printing/label-printing.utils'
import { usePrintLabels } from '@/frontend/features/printing/labels/use-print-labels'
import { useViewerRegion } from '@/frontend/features/region/region-context'
import { usePatchConsultation } from '@/frontend/hooks/api/consultations/use-patch-consultation'
import { usePatchAppointment } from '@/frontend/hooks/api/use-patch-appointment'
import { cn } from '@/frontend/lib/utils'

// ---------------------------------------------------------------------------
// Sub-sections
// ---------------------------------------------------------------------------

/** Vitals chips in a grid. */
function VitalsSection({ vitals }: { vitals: ConsultationWithRelations['vitals'] }) {
  if (!vitals) return null

  const hasAny =
    vitals.weight ||
    vitals.temperature ||
    vitals.heartRate ||
    vitals.respiratoryRate ||
    vitals.bodyConditionScore ||
    vitals.painScore != null
  if (!hasAny) return null

  return (
    <div className="flex items-center gap-3 border-t px-4 py-3">
      <HeartPulse className="size-3.5 shrink-0 text-muted-foreground" />
      <div className="flex flex-1 flex-wrap gap-2">
        <VitalsChips vitals={vitals} />
      </div>
    </div>
  )
}

/** Every treatment recorded in this consultation, one row each. */
function TreatmentsSection({
  treatments,
  consultation,
}: {
  treatments: (TreatmentWithRelations & TreatmentPrescriptionContext)[]
  consultation: ConsultationWithRelations
}) {
  if (treatments.length === 0) return null

  return (
    <ul>
      {treatments.map((t) => (
        <li key={t.id} className="border-t">
          <TreatmentRow
            treatment={t}
            className="py-2"
            menu={
              t.treatmentType === 'prescription' ? (
                <ConsultationTreatmentMenu rx={t} consultation={consultation} />
              ) : undefined
            }
          />
        </li>
      ))}
    </ul>
  )
}

/** Icon per note type — follow-ups get a distinct calendar icon, internal gets a lock. */
const NOTE_ICONS: Record<string, typeof FileText> = {
  soap: ClipboardList,
  follow_up: CalendarClock,
  internal: EyeOff,
}

/** Note section — icon in left gutter, content alongside. */
function NoteSection({ note }: { note: NoteWithStaff }) {
  const Icon = NOTE_ICONS[note.type] ?? FileText

  // Skip notes with no meaningful content and no files
  if (isNoteEmpty(note) && note.files.length === 0) return null

  return (
    <div className="flex items-center gap-3 border-t px-4 py-3">
      <Icon className="size-3.5 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 space-y-2">
        <ExpandableContent>
          <NoteContent note={note} />
        </ExpandableContent>
      </div>
    </div>
  )
}

/** Files attached to notes in this consultation. */
function FilesSection({ notes }: { notes: NoteWithStaff[] }) {
  const allFiles = notes.flatMap((n) => n.files)
  if (allFiles.length === 0) return null

  return (
    <div className="flex items-center gap-3 border-t px-4 py-3">
      <Paperclip className="size-3.5 shrink-0 text-muted-foreground" />
      <ExpandableContent>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
          {allFiles.map((file) => (
            <FileDownloadLink key={file.id} fileId={file.id} fileName={file.fileName} />
          ))}
        </div>
      </ExpandableContent>
    </div>
  )
}

/** Grand total of all priced treatments in this consultation. Hidden when no treatments have a price. */
function TotalSection({ treatments }: { treatments: TreatmentWithRelations[] }) {
  const orders = treatments
    .map(getPricedOrder)
    .filter((o) => o !== null)
    // A line we can't denominate is left out of the total rather than throwing
    // mid-render.
    .filter((o) => getClinic(o.clinicId) !== null)
  if (orders.length === 0) return null

  const total = orders.reduce<number>((sum, o) => sum + o.totalPrice, 0)
  // A consultation's orders share a clinic in practice, so the first one denominates
  // the total. Nothing enforces that structurally — but a total summed across two
  // currencies is meaningless whichever of them we label it with.
  const { clinicId } = orders[0]

  return (
    <div className="flex items-center justify-between border-t px-4 py-3">
      <span className="flex items-center gap-3 text-sm font-medium">
        <Banknote className="size-3.5 shrink-0 text-muted-foreground" />
        Total
      </span>
      <span className="font-semibold tabular-nums">{formatClinicPrice(total, clinicId)}</span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main consultation entry
// ---------------------------------------------------------------------------

interface ConsultationEntryProps {
  consultation: ConsultationWithRelations
  /** Dated on a later calendar day — styled as provisional. */
  isFuture?: boolean
}

function treatmentSortRank(t: TreatmentWithRelations): number {
  if (t.treatmentType === 'vaccination') return 0
  if (t.treatmentType !== 'prescription') return 3
  return t.isExternal ? 2 : 1
}

function getConsultationTitle(consultation: ConsultationWithRelations): string {
  if (consultation.appointment) {
    return (
      APPOINTMENT_TYPES_BY_ID[consultation.appointment.appointmentTypeId]?.name ??
      consultation.appointment.appointmentTypeId
    )
  }
  return 'Consultation'
}

export function ConsultationEntry({ consultation, isFuture = false }: ConsultationEntryProps) {
  const status = deriveConsultationStatus(consultation)
  // An unresolvable clinic id (e.g. a legacy Digitail row) is unknown, not remote.
  const location = consultation.clinicId ? consultation.clinic?.name : 'Remote'
  const { open } = useActiveConsultation()
  const { mutateAsync: patchConsultation } = usePatchConsultation()
  const patchAppointment = usePatchAppointment({ consultationId: consultation.id })

  // Show check-in as a happy-path convenience when the consultation was pre-prepped
  const appointmentStatus = consultation.appointment?.status ?? null
  const activityLog = consultation.appointment?.activityLog ?? []
  const [historyOpen, setHistoryOpen] = useState(false)
  const canCheckIn =
    status === 'in_progress' &&
    !!appointmentStatus &&
    APPOINTMENT_STATUS_TRANSITIONS[appointmentStatus].has('checked-in')

  async function handleReopen() {
    try {
      await patchConsultation({ id: consultation.id, body: { action: 'reopen' } })
      toast.success('Consultation unlocked')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to reopen consultation')
    }
  }

  // Vaccines, internal scripts, external scripts, then procedures; oldest first within each.
  const sorted = [...consultation.treatments].sort(
    (a, b) => treatmentSortRank(a) - treatmentSortRank(b) || a.createdAt.localeCompare(b.createdAt)
  )

  return (
    <div
      className={cn('overflow-hidden rounded-xl bg-card ring-1 ring-foreground/10 border-t-2')}
      style={
        consultation.appointment
          ? { borderTopColor: getCategoryStyle(consultation.appointment.appointmentTypeId).border }
          : undefined
      }
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-4 px-4 pt-3 pb-2"
        style={
          consultation.appointment
            ? {
                backgroundColor: `${getCategoryStyle(consultation.appointment.appointmentTypeId).bg}26`,
              }
            : undefined
        }
      >
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center gap-1">
              <span className="text-headline-sm">{getConsultationTitle(consultation)}</span>
              {(() => {
                const apptTypeId = consultation.appointment?.appointmentTypeId
                const category = apptTypeId ? APPOINTMENT_TYPES_BY_ID[apptTypeId]?.category : null
                const CategoryIcon = category ? getCategoryStyle(apptTypeId!).icon : Stethoscope
                const iconColour = category ? getCategoryStyle(apptTypeId!).text : undefined
                return (
                  <CategoryIcon
                    className="size-3"
                    style={iconColour ? { color: iconColour } : undefined}
                  />
                )
              })()}
            </span>
            {status === 'in_progress' && (
              <Badge variant="pending" size="xs">
                In Progress
              </Badge>
            )}
            {isFuture && <UpcomingBadge size="xs" />}
            {status === 'finalised' && (
              <span title="Locked" className="text-sm">
                🔒
              </span>
            )}
            {/* Surface a no-show badge so a consultation started for an appointment
                that never happened has clear context in the timeline. */}
            {appointmentStatus === 'no-show' && (
              <AppointmentStatusBadge status="no-show" size="xs" />
            )}
            {activityLog.length > 0 && (
              <ExpandBadge
                open={historyOpen}
                onToggle={() => setHistoryOpen(!historyOpen)}
                icon={<History />}
              >
                History ({activityLog.length})
              </ExpandBadge>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="size-3 shrink-0" />
              <FormattedDateTime
                value={consultation.appointment?.startTime ?? consultation.createdAt}
                clinicId={consultation.clinicId}
              />
            </span>
            {consultation.staff && (
              <span className="flex items-center gap-1">
                <Stethoscope className="size-3 shrink-0" />
                {consultation.staff.firstName} {consultation.staff.lastName}
              </span>
            )}
            {location && (
              <span className="flex items-center gap-1">
                {consultation.clinicId ? (
                  <Building2 className="size-3 shrink-0" />
                ) : (
                  <Globe className="size-3 shrink-0" />
                )}
                {location}
              </span>
            )}
          </div>
          {consultation.appointment?.visitReason && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <NotebookPen className="size-3 shrink-0" />
              <p>{consultation.appointment.visitReason}</p>
            </div>
          )}
          {consultation.appointment?.userNotes && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <MessageCircleMore className="size-3 shrink-0" />
              <p>{consultation.appointment.userNotes}</p>
            </div>
          )}
          {consultation.reason && (
            <div className="flex items-center gap-1 text-sm text-muted-foreground">
              <NotebookPen className="size-3 shrink-0" />
              <p>{consultation.reason}</p>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {consultation.appointment && canCheckIn && (
            <Button
              variant="ghost"
              disabled={patchAppointment.isPending}
              onClick={() =>
                patchAppointment.mutate({
                  id: consultation.appointment!.id,
                  status: 'checked-in',
                })
              }
            >
              <LogIn className="size-3" />
              Check In
            </Button>
          )}
          {status === 'finalised' && (
            <Tooltip>
              <TooltipTrigger
                render={
                  <ConfirmButton
                    variant="ghost"
                    size="icon-sm"
                    heading="Reopen consultation?"
                    description="This will unlock the consultation for further editing. It can be locked again when you're done."
                    confirmLabel="Reopen"
                    onConfirm={handleReopen}
                  >
                    <LockOpen className="size-4 text-warning-fg" />
                  </ConfirmButton>
                }
              />
              <TooltipContent>Reopen consultation</TooltipContent>
            </Tooltip>
          )}
          {status === 'in_progress' && <Button onClick={() => open(consultation.id)}>Edit</Button>}
        </div>
      </div>
      {historyOpen && activityLog.length > 0 && (
        <div
          className="border-t px-4 py-2"
          style={
            consultation.appointment
              ? {
                  backgroundColor: `${getCategoryStyle(consultation.appointment.appointmentTypeId).bg}26`,
                }
              : undefined
          }
        >
          <AppointmentActivityLog activityLog={activityLog} clinicId={consultation.clinicId} />
        </div>
      )}

      {/* Content sections */}
      <VitalsSection vitals={consultation.vitals} />
      {consultation.notes.map((n) => (
        <NoteSection key={n.id} note={n} />
      ))}
      <FilesSection notes={consultation.notes} />
      <TreatmentsSection treatments={sorted} consultation={consultation} />
      <TotalSection treatments={consultation.treatments} />
    </div>
  )
}

/** Actions menu for treatments within a read-only consultation card — re-prescribe and print. */
function ConsultationTreatmentMenu({
  rx,
  consultation,
}: {
  rx: TreatmentWithRelations & TreatmentPrescriptionContext
  consultation: ConsultationWithRelations
}) {
  const refill = useRefillPrescription({ rx, patientId: rx.patientId })
  const represcribe = useReprescribePrescription({ rx, patientId: rx.patientId })
  const script = useGeneratePrescriptionScript({ rx, patientId: rx.patientId })
  const { print, isPrinting } = usePrintLabels()
  const { region: activeRegion } = useViewerRegion()

  /** Generate and preview the dispensing label for this prescription. */
  async function handlePrintLabel() {
    const filename = `label-${rx.productInfo.name.toLowerCase().replace(/\s+/g, '-')}.pdf`
    await print(
      [
        committedTreatmentToLabelData({
          treatment: rx,
          patient: consultation.patient,
          ownerName: formatName(consultation.patient.owner),
          clinic: consultation.clinic,
          // This consultation's recorded weight, matching what VitalsSection shows,
          // so a printed label agrees with the weight visible on the same card.
          weight: consultation.vitals?.weight
            ? formatWeight(Number.parseFloat(consultation.vitals.weight), activeRegion)
            : null,
          // The label goes home with the owner, so its dates follow the
          // dispensing clinic and the owner's region, not the printing vet's.
          fmt: resolveDateFormat(consultation.clinicId, consultation.patient.owner.region),
        }),
      ],
      filename
    )
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger render={<Button variant="ghost" size="icon-sm" />}>
          <EllipsisVertical className="size-3.5" />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" side="bottom">
          {refill.menuItem}
          <DropdownMenuItem onClick={represcribe.trigger}>
            <CopyPlus className="size-3.5" />
            Re-prescribe
          </DropdownMenuItem>
          {/* Internal prescriptions get a dispensing label; external ones get a written script. */}
          {!rx.isExternal && (
            <DropdownMenuItem disabled={isPrinting} onClick={handlePrintLabel}>
              <Printer className="size-3.5" />
              Print Label
            </DropdownMenuItem>
          )}
          {rx.isExternal && (
            <DropdownMenuItem onClick={script.trigger}>
              <Printer className="size-3.5" />
              Print Script
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {refill.dialog}
      {represcribe.dialog}
      {script.dialog}
    </>
  )
}
